import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  ArrowLeft, Download, TrendingUp, Users, Tag, Link as LinkIcon,
  Award, Sparkles, ThumbsUp, AlertTriangle, DollarSign, MousePointer,
  Target, Eye, ExternalLink, FileImage, FileText,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import CreativeImage from '../../components/CreativeImage';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ROAS-based performance tag thresholds.
// Order matters — first match wins.
const PERF_TAGS = [
  { min: 5.0,  label: 'Top Performer',    icon: Award,       accent: 'from-emerald-500 to-teal-500', ring: 'ring-emerald-300', text: 'text-emerald-100', dot: 'bg-emerald-400' },
  { min: 3.0,  label: 'Great Choice',     icon: Sparkles,    accent: 'from-blue-500 to-indigo-500',  ring: 'ring-blue-300',    text: 'text-blue-100',    dot: 'bg-blue-400' },
  { min: 1.5,  label: 'Good Option',      icon: ThumbsUp,    accent: 'from-amber-500 to-orange-500', ring: 'ring-amber-300',   text: 'text-amber-100',   dot: 'bg-amber-400' },
  { min: -Infinity, label: 'Needs Improvement', icon: AlertTriangle, accent: 'from-rose-500 to-red-500', ring: 'ring-rose-300',   text: 'text-rose-100',    dot: 'bg-rose-400' },
];

const pickTag = (roas) => PERF_TAGS.find((t) => (Number(roas) || 0) >= t.min) || PERF_TAGS[PERF_TAGS.length - 1];

const fmtInr = (v) => {
  const n = Number(v || 0);
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${n.toLocaleString('en-IN')}`;
};
const fmtNum = (v) => Number(v || 0).toLocaleString('en-IN');
const fmtPct = (v) => `${(Number(v || 0)).toFixed(1)}%`;

const truncateUrl = (url, max = 42) => {
  if (!url) return '';
  return url.length > max ? `${url.slice(0, max - 1)}…` : url;
};

const CompareCampaigns = ({ selectedIds = [], onBack, showToast }) => {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';

  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const gridRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [{ data: allCampaigns }, { data: allBrands }] = await Promise.all([
          axios.get(`${API}/campaigns-data`),
          axios.get(`${API}/brands`),
        ]);
        if (!mounted) return;
        setCampaigns(allCampaigns || []);
        setBrands(allBrands || []);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to load compare data', err);
        showToast?.('Failed to load compare data', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [showToast]);

  const brandMap = useMemo(() => {
    const map = {};
    brands.forEach((b) => { map[b.id] = b; });
    return map;
  }, [brands]);

  const items = useMemo(() => {
    if (!selectedIds.length || !campaigns.length) return [];
    return selectedIds
      .map((id) => campaigns.find((c) => c.id === id))
      .filter(Boolean);
  }, [selectedIds, campaigns]);

  // Determine winner per numeric metric to highlight best in each column
  const bestOf = useMemo(() => {
    const keys = ['sales', 'net_spends', 'ctr', 'delivery_rate', 'roas'];
    const out = {};
    keys.forEach((k) => {
      const values = items.map((c) => Number(c?.[k] || 0));
      if (values.length === 0) return;
      out[k] = {
        max: Math.max(...values),
        min: Math.min(...values),
      };
    });
    return out;
  }, [items]);

  const handleExport = async (format) => {
    if (!gridRef.current) return;
    try {
      setExporting(true);
      const ts = new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-');
      const dataUrl = await toPng(gridRef.current, {
        backgroundColor: '#0f172a',
        pixelRatio: 2,
        cacheBust: true,
        skipFonts: true,
      });
      if (format === 'pdf') {
        const img = new Image();
        img.src = dataUrl;
        await new Promise((res) => { img.onload = res; if (img.complete) res(); });
        const orientation = img.width >= img.height ? 'landscape' : 'portrait';
        const pdf = new jsPDF({ orientation, unit: 'pt', format: [img.width, img.height], compress: true });
        pdf.addImage(dataUrl, 'PNG', 0, 0, img.width, img.height);
        pdf.save(`campaign-compare-${ts}.pdf`);
      } else {
        const link = document.createElement('a');
        link.download = `campaign-compare-${ts}.png`;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      showToast?.(`Exported comparison as ${format.toUpperCase()}`, 'success');
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      showToast?.('Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-slate-400 text-sm" data-testid="compare-loading">Loading comparison…</div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="p-8 text-slate-400 text-sm" data-testid="compare-empty">
        <p className="mb-4">No campaigns selected for comparison.</p>
        <button
          onClick={onBack}
          data-testid="compare-back-empty"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm"
        >
          <ArrowLeft size={14} /> Back to Campaign Analytics
        </button>
      </div>
    );
  }

  const columns = Math.min(items.length, 5);

  return (
    <div className="p-6" data-testid="compare-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200"
            data-testid="compare-back-btn"
            title="Back to Campaign Analytics"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white flex items-center gap-2">
              <TrendingUp size={18} className="text-teal-400" />
              Comparing {items.length} campaign{items.length === 1 ? '' : 's'}
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">Side-by-side performance breakdown with ROAS-based recommendations.</p>
          </div>
        </div>

        {!isViewer && (
          <div className="flex items-center gap-2" data-testid="compare-export-toolbar">
            <button
              onClick={() => handleExport('png')}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sm text-slate-200 disabled:opacity-50"
              data-testid="compare-export-png"
            >
              <FileImage size={14} /> Export PNG
            </button>
            <button
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-sm text-white disabled:opacity-50"
              data-testid="compare-export-pdf"
            >
              <FileText size={14} /> Export PDF
            </button>
          </div>
        )}
      </div>

      {/* Compare grid */}
      <div
        ref={gridRef}
        className="grid gap-4 p-1"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(260px, 1fr))` }}
        data-testid="compare-grid"
      >
        {items.map((c) => {
          const brand = brandMap[c.brand_id];
          const tag = pickTag(c.roas);
          const TagIcon = tag.icon;
          const best = (k) => bestOf[k] && Number(c[k] || 0) === bestOf[k].max && bestOf[k].max > 0;
          const worst = (k) => bestOf[k] && Number(c[k] || 0) === bestOf[k].min && bestOf[k].max !== bestOf[k].min;

          return (
            <div
              key={c.id}
              className="bg-slate-800/80 border border-slate-700 rounded-2xl overflow-hidden flex flex-col shadow-lg"
              data-testid={`compare-card-${c.id}`}
            >
              {/* Creative + ROAS badge */}
              <div className="relative aspect-square bg-slate-900 overflow-hidden">
                <CreativeImage
                  src={c.image || c.creative}
                  alt={c.name}
                  className="w-full h-full object-cover"
                />
                {c.roas != null && (
                  <div
                    className={`absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${tag.accent} shadow-lg ring-2 ${tag.ring} ring-opacity-30`}
                    data-testid={`compare-roas-badge-${c.id}`}
                  >
                    <Target size={12} className="text-white" />
                    <span className="text-xs font-bold text-white">{Number(c.roas).toFixed(2)}x ROAS</span>
                  </div>
                )}
                {/* Performance tag */}
                <div
                  className={`absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${tag.accent} shadow-lg`}
                  data-testid={`compare-perf-tag-${c.id}`}
                >
                  <TagIcon size={12} className="text-white" />
                  <span className={`text-[11px] font-bold ${tag.text} text-white`}>{tag.label}</span>
                </div>
              </div>

              {/* Body */}
              <div className="p-4 flex-1 flex flex-col gap-3">
                <div>
                  <h3 className="text-sm font-bold text-white line-clamp-2" title={c.name}>{c.name || 'Untitled'}</h3>
                  {brand && (
                    <div className="mt-1.5 inline-flex items-center gap-1.5">
                      <span
                        className="inline-block w-2 h-2 rounded-full"
                        style={{ backgroundColor: brand.color || '#64748b' }}
                      />
                      <span className="text-[11px] text-slate-400 font-medium">{brand.name}</span>
                      {c.date && <span className="text-[11px] text-slate-500">· {c.date}</span>}
                    </div>
                  )}
                </div>

                {/* Metric strip */}
                <div className="grid grid-cols-2 gap-2">
                  <MetricTile
                    icon={<DollarSign size={12} className="text-teal-400" />}
                    label="Revenue"
                    value={fmtInr(c.sales)}
                    highlight={best('sales') ? 'best' : (worst('sales') ? 'worst' : null)}
                    testid={`compare-revenue-${c.id}`}
                  />
                  <MetricTile
                    icon={<DollarSign size={12} className="text-red-400" />}
                    label="Spend"
                    value={fmtInr(c.net_spends)}
                    highlight={best('net_spends') ? 'best' : (worst('net_spends') ? 'worst' : null)}
                    testid={`compare-spend-${c.id}`}
                  />
                  <MetricTile
                    icon={<MousePointer size={12} className="text-yellow-400" />}
                    label="CTR"
                    value={fmtPct(c.ctr)}
                    highlight={best('ctr') ? 'best' : (worst('ctr') ? 'worst' : null)}
                    testid={`compare-ctr-${c.id}`}
                  />
                  <MetricTile
                    icon={<Eye size={12} className="text-emerald-400" />}
                    label="Delivery"
                    value={fmtPct(c.delivery_rate)}
                    highlight={best('delivery_rate') ? 'best' : (worst('delivery_rate') ? 'worst' : null)}
                    testid={`compare-delivery-${c.id}`}
                  />
                </div>

                {/* Meta */}
                <div className="mt-1 flex flex-col gap-1.5 text-[11px]">
                  {c.cohort && (
                    <div className="inline-flex items-center gap-2" data-testid={`compare-cohort-${c.id}`}>
                      <Users size={12} className="text-cyan-400 flex-shrink-0" />
                      <span className="text-slate-400">Cohort:</span>
                      <span className="text-white font-medium truncate">{c.cohort}</span>
                    </div>
                  )}
                  {c.attribution_setting && (
                    <div className="inline-flex items-center gap-2" data-testid={`compare-attribution-${c.id}`}>
                      <Tag size={12} className="text-pink-400 flex-shrink-0" />
                      <span className="text-slate-400">Attribution:</span>
                      <span className="text-white font-medium truncate">{c.attribution_setting}</span>
                    </div>
                  )}
                  {c.landing_page && (
                    <a
                      href={c.landing_page}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={c.landing_page}
                      className="inline-flex items-center gap-2 hover:text-teal-300 group"
                      data-testid={`compare-landing-${c.id}`}
                    >
                      <LinkIcon size={12} className="text-teal-400 flex-shrink-0" />
                      <span className="text-slate-400">Landing:</span>
                      <span className="text-teal-300 group-hover:text-teal-200 font-medium truncate">
                        {truncateUrl(c.landing_page)}
                      </span>
                      <ExternalLink size={10} className="text-slate-500 group-hover:text-teal-300 flex-shrink-0" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-5 p-4 bg-slate-800/50 border border-slate-700 rounded-xl" data-testid="compare-legend">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={14} className="text-teal-400" />
          <span className="text-xs font-bold text-slate-200">Performance tag thresholds (based on ROAS)</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {PERF_TAGS.map((t) => {
            const Icon = t.icon;
            const label = t.min === -Infinity ? '< 1.5x' : t.min === 1.5 ? '≥ 1.5x' : t.min === 3 ? '≥ 3.0x' : '≥ 5.0x';
            return (
              <div key={t.label} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${t.accent} text-white text-[11px] font-medium`}>
                <Icon size={12} />
                <span>{t.label}</span>
                <span className="opacity-80">{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MetricTile = ({ icon, label, value, highlight, testid }) => {
  const border = highlight === 'best'
    ? 'border-emerald-500/60 bg-emerald-500/10'
    : highlight === 'worst'
      ? 'border-rose-500/40 bg-rose-500/5'
      : 'border-slate-700 bg-slate-900/60';
  return (
    <div className={`rounded-lg border ${border} px-2.5 py-2`} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">{label}</span>
        {highlight === 'best' && (
          <span className="ml-auto text-[9px] font-bold text-emerald-400 bg-emerald-500/20 px-1 rounded">BEST</span>
        )}
      </div>
      <p className="text-sm font-bold text-white">{value}</p>
    </div>
  );
};

export default CompareCampaigns;
